import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChildren
} from '@angular/core';

import { LoginFacade, ProductCategoriesFacade } from '@ridango/components';
import { SelectableCheckableItemComponent } from '@ridango/components/lib/core-ui/basic';
import { ProductCategory, ProductCategoryTypes } from '@ridango/core';

import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import Env from '@ridango/core/env';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';

@Component({
    selector: 'rwidget-product-category-selector',
    templateUrl: './product-category-selector.component.html',
    styleUrls: ['./product-category-selector.component.scss']
})
export class ProductCategorySelectorComponent implements OnInit, OnDestroy {
    private componentDestroyed$ = new Subject<void>();

    @Input() type: string;
    @Input() isEmpty = false;
    @Input() step = 0;
    @Input() locale;
    categories$: Observable<ProductCategory[]>;
    selectedCategory$: Observable<any>;

    // TODO: check if this is needed
    categories = [];
    selectedCategory;

    @ViewChildren(SelectableCheckableItemComponent) focusItems: QueryList<any>;

    constructor(
        private productCategoriesFacade: ProductCategoriesFacade,
        @Inject('ENV') public environment: Env
    ) {}

    ngOnInit() {
        this.categories$ = this.productCategoriesFacade.getCategoriesByType(this.type);
        // for WCAG purposes only
        this.categories$.pipe(takeUntil(this.componentDestroyed$)).subscribe((cat) => (this.categories = cat));
    }

    ngOnDestroy() {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    productCategorySelected(category: ProductCategory) {
        this.productCategoriesFacade.setSelectedCategory(category);
        this.selectedCategory$ = this.productCategoriesFacade.getSelectedCategoryByType(this.type);
        // for WCAG purposes only
        this.selectedCategory$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((cat) => (this.selectedCategory = cat));

        if (this.environment.FILTER_PRODUCT_CATEGORY_BY_AREA && category.type === ProductCategoryTypes.AREA) {
            // set product categories for the area selected
            this.productCategoriesFacade
                .getSubCategories(category.id)
                .pipe(take(1))
                .subscribe((subctegories) => {
                    const productCategories = subctegories.map((subcategory) => subcategory.id);
                    this.productCategoriesFacade.setProductCategories(productCategories);
                });
        }
    }

    selectLeft(pc: ProductCategory) {
        this.setFocusToSibling(pc, -1);
    }

    selectRight(pc: ProductCategory) {
        this.setFocusToSibling(pc, 1);
    }

    setFocusToSibling(pc: ProductCategory, direction: number) {
        let destElId;

        this.categories.some((c, i) => {
            if (pc.id === c.id) {
                destElId = i + direction;
                return true;
            }
        });

        if (destElId < 0) {
            destElId = this.focusItems.length - 1;
        } else if (destElId >= this.focusItems.length) {
            destElId = 0;
        }

        this.productCategorySelected(this.categories[destElId]);
        setTimeout(() => this.focusItems.toArray()[destElId].selectCheckItem.nativeElement.focus());
    }

    focus(pc: ProductCategory) {
        this.focusItems.toArray()[this.findElementIndex(pc)].selectCheckItem.nativeElement.classList.add('focus');
    }

    blur(pc: ProductCategory) {
        this.focusItems.toArray()[this.findElementIndex(pc)].selectCheckItem.nativeElement.classList.remove('focus');
    }

    findElementIndex(pc: ProductCategory) {
        return this.categories.findIndex((pcs: ProductCategory) => pcs.id === pc.id);
    }

    getTabIndex(pc: ProductCategory) {
        if (this.selectedCategory) {
            if (this.selectedCategory.id === pc.id) {
                // Selected item
                return 0;
            }
        } else if (this.categories[0].id === pc.id) {
            // First item
            return 0;
        }

        return -1;
    }
}
